import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Building2, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import PageShell, { PageHero } from "./PageShell";
import { company } from "../mock";
import api, { visitorLang } from "../lib/api";
import PhoneInput, { defaultDial, fullPhone, phoneValid } from "../components/PhoneInput";
import CallbackFields, { CALLBACK_SLOTS } from "../components/CallbackFields";

import { tr, localeTag } from "../i18n";
export default function ContactPage() {
  const [params] = useSearchParams();
  const offer = (params.get("offer") || "").slice(0, 200);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", dial: defaultDial(), offer,
    message: offer ? tr("I would like to request this offer: {v0}", { v0: offer }) : "",
    callbackRequested: false, callbackRegion: "United Kingdom", callbackDate: "", callbackTime: CALLBACK_SLOTS[0],
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!phoneValid(form.phone)) {
      setError(tr("Please enter a valid phone number."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { dial, ...rest } = form;
      await api.post("/leads", { ...rest, phone: fullPhone(dial, form.phone), clientType: "Enquiry", mode: "contact", lang: visitorLang() });
      setDone(true);
    } catch (err) {
      setError(tr("Could not send your message. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const details = [
    { icon: Building2, label: tr("Firm"), value: `${company.legalName} · Reg. ${company.registration}` },
    { icon: MapPin, label: tr("Registered office"), value: company.address },
    { icon: Mail, label: tr("Email"), value: company.email },
    { icon: Phone, label: tr("Telephone (United Kingdom)"), value: company.phone },
    { icon: Phone, label: `${tr("Telephone")} · ${company.phoneDENote}`, value: company.phoneDE },
  ];

  return (
    <PageShell>
      <PageHero
        eyebrow={tr("Contact")}
        title={tr("Talk to our team")}
        intro={tr("Whether you're opening an account or have a question about the platform, we're here to help.")}
      />
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-[28px] text-[#0b1f3a] mb-6">{tr("Get in touch")}</h2>
            <div className="space-y-5">
              {details.map((d) => (
                <div key={d.label} className="flex gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full bg-[#f6f2e9] flex items-center justify-center">
                    <d.icon size={19} className="text-[#c2a15a]" />
                  </div>
                  <div>
                    <p className="text-[12px] uppercase tracking-wide text-[#0b1f3a]/45">{d.label}</p>
                    <p className="text-[#0b1f3a] text-[15px] mt-0.5">{d.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[13px] text-[#0b1f3a]/45 mt-8 leading-relaxed border-t border-[#0b1f3a]/10 pt-6">
              {company.legalName} {tr("is authorised and regulated by the")} {company.regulator} {tr("(Firm No.")} {company.sraFirm}).
            </p>

            {/* German customers / German phone line */}
            <div className="mt-8 rounded-2xl bg-[#f6f2e9]/60 ring-1 ring-[#0b1f3a]/10 p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-[#0b1f3a] flex items-center justify-center shrink-0">
                  <Phone size={19} className="text-[#c2a15a]" />
                </div>
                <h3 className="font-display text-[20px] text-[#0b1f3a]">
                  {tr("For our German customers")}
                </h3>
              </div>
              <p className="text-[#0b1f3a]/70 text-[14.5px] leading-relaxed">
                {tr("Our German savers and prospective customers can reach us on a German phone number with the country code")}{" "}
                <span className="font-semibold text-[#0b1f3a]">+49</span>{tr(". If you have any questions about our fixed-term deposit offers, interest rates or terms, you are welcome to call us back easily whenever it suits you.")}
              </p>
              <a
                href={`tel:${company.phoneDE.replace(/[^+\d]/g, "")}`}
                className="inline-block mt-3 font-display text-[22px] text-[#0b1f3a] hover:text-[#c2a15a] transition-colors"
              >
                {company.phoneDE}
              </a>
              <p data-testid="contact-phone-de-note" className="text-[12px] uppercase tracking-wide text-[#c2a15a] font-medium mt-1">
                {company.phoneDENote}
              </p>
              <p className="text-[13px] text-[#0b1f3a]/50 leading-relaxed mt-3">
                {tr("By using a German phone number, possible additional costs for calls to foreign numbers can be avoided. The actual costs depend on your telephone provider and tariff. Our aim is to make telephone communication as simple, transparent and cost-effective as possible for our German customers.")}
              </p>
              <p className="text-[11.5px] text-[#0b1f3a]/40 leading-relaxed mt-3 italic">
                {tr("A German phone number alone does not imply a German place of business, a banking licence or any regulatory authorisation in Germany.")}
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#0b1f3a] p-8">
            {done ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 mx-auto rounded-full bg-white/10 flex items-center justify-center mb-4">
                  <CheckCircle2 size={32} className="text-[#c2a15a]" />
                </div>
                <h3 className="font-display text-white text-[24px]">{tr("Message sent")}</h3>
                <p className="text-white/60 mt-2">
                  {form.callbackRequested
                    ? tr("Thank you — we'll call you back on {v0}, {v1}.", { v0: new Date(form.callbackDate).toLocaleDateString(localeTag(), { day: "numeric", month: "long", year: "numeric" }), v1: tr(form.callbackTime) })
                    : tr("Thank you — we'll respond as soon as we can.")}
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <p className="text-[#c2a15a] text-[11px] tracking-[0.25em] uppercase">{tr("Send us a message")}</p>
                {form.offer && (
                  <div data-testid="contact-selected-offer" className="rounded-xl bg-white/10 ring-1 ring-[#c2a15a]/40 px-4 py-3 text-[13px] text-white/85">
                    <span className="block text-[11px] uppercase tracking-wide text-[#c2a15a] mb-0.5">{tr("Selected offer")}</span>
                    {form.offer}
                  </div>
                )}
                <input required value={form.name} onChange={update("name")} placeholder={tr("Full name")} className="rh-input" />
                <input required type="email" value={form.email} onChange={update("email")} placeholder={tr("Email")} className="rh-input" />
                <PhoneInput testid="contact-phone-input" dial={form.dial} number={form.phone}
                  onDial={(v) => setForm({ ...form, dial: v })} onNumber={(v) => setForm({ ...form, phone: v })} />
                <textarea required value={form.message} onChange={update("message")} placeholder={tr("How can we help?")} rows={4} className="rh-input resize-none" />
                <CallbackFields form={form} setForm={setForm} />
                {error && <p data-testid="contact-error" className="text-[13px] text-red-300">{error}</p>}
                <button type="submit" disabled={busy} className="group w-full inline-flex items-center justify-center gap-2 bg-[#c2a15a] hover:bg-[#d8bd82] disabled:opacity-60 text-[#0b1f3a] font-semibold uppercase tracking-wide text-[14px] px-6 py-4 rounded-full transition-colors">
                  {busy ? <Loader2 size={18} className="animate-spin" /> : (<>{tr("Send message")} <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" /></>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
